import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';

import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { type AlbumMeta } from '@/lib/album-utils';
import { seedFromHex, getCardRotation } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

interface AlbumCoverCardProps {
  album: AlbumMeta;
  /** 'feed' = large single-column (with author), 'grid' = small for grid/row */
  variant?: 'feed' | 'grid';
  index?: number;
}

/**
 * Muted, desaturated cover colors — like real cloth/leather album covers.
 * Each album gets a deterministic color from this palette.
 */
const COVER_COLORS = [
  { bg: '#7A8B6F', dark: '#5C6B52', text: '#D4DCCC' }, // sage green
  { bg: '#8B6F6F', dark: '#6B5252', text: '#DCC8C8' }, // dusty rose / burgundy
  { bg: '#6F7A8B', dark: '#52596B', text: '#C8D0DC' }, // slate blue
  { bg: '#8B7F6F', dark: '#6B6052', text: '#DCD4C8' }, // warm tan / leather
  { bg: '#7A6F8B', dark: '#5C526B', text: '#D0C8DC' }, // muted plum
  { bg: '#6F8B7A', dark: '#526B5C', text: '#C8DCD0' }, // eucalyptus
  { bg: '#8B7A6F', dark: '#6B5C52', text: '#DCD0C8' }, // brown leather
  { bg: '#6F808B', dark: '#52626B', text: '#C8D4DC' }, // dusty steel
];

/** Get a deterministic cover color palette from the event ID. */
function getCoverColor(id: string) {
  const idx = seedFromHex(id, 12, 16) % COVER_COLORS.length;
  return COVER_COLORS[idx];
}

/**
 * Album cover card — looks like a real old-school physical photo album.
 *
 * Features:
 * - Thick textured cloth/leather cover with grain pattern
 * - Photo window cutout with decorative L-shaped corner mounts
 * - Embossed title text with subtle depth
 * - Visible spine with horizontal ridged lines
 * - Page edges peeking from the right
 * - Heavy shadow suggesting a real 3D object
 */
export function AlbumCoverCard({ album, variant = 'feed', index = 0 }: AlbumCoverCardProps) {
  const navigate = useNavigate();
  const isLarge = variant === 'feed';
  const showAuthor = variant === 'feed';

  const author = useAuthor(showAuthor ? album.pubkey : undefined);
  const displayName = author.data?.metadata?.name ?? genUserName(album.pubkey);
  const avatar = author.data?.metadata?.picture;

  const rotation = isLarge ? getCardRotation(album.event.id) : (seedFromHex(album.event.id, 0, 4) % 7) - 3;
  const coverColor = getCoverColor(album.event.id);
  const photoCount = album.photoIds.length;

  const handleClick = () => {
    const npub = nip19.npubEncode(album.pubkey);
    navigate(`/${npub}/albums/${album.dTag}`);
  };

  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const npub = nip19.npubEncode(album.pubkey);
    navigate(`/${npub}`);
  };

  // Spine dimensions
  const spineW = isLarge ? 20 : 14;
  // Photo window inset from cover edges
  const windowInset = isLarge ? 20 : 14;

  return (
    <div className="relative">
      <div
        className={cn(
          'relative cursor-pointer',
          'transition-all duration-200 ease-out',
          'hover:-translate-y-1.5',
          'active:scale-[0.97]',
          isLarge
            ? 'shadow-[0_6px_24px_rgba(30,20,50,0.22),0_2px_6px_rgba(30,20,50,0.12)]'
            : 'shadow-[0_4px_14px_rgba(30,20,50,0.18),0_1px_4px_rgba(30,20,50,0.10)]',
          isLarge
            ? 'hover:shadow-[0_10px_36px_rgba(30,20,50,0.28),0_4px_10px_rgba(30,20,50,0.14)]'
            : 'hover:shadow-[0_8px_24px_rgba(30,20,50,0.24),0_3px_8px_rgba(30,20,50,0.12)]',
        )}
        style={{ transform: `rotate(${rotation}deg)` }}
        onClick={handleClick}
      >
        {/* Book structure — spine + cover */}
        <div className="flex">
          {/* Spine — darker shade with horizontal ridges */}
          <div
            className="relative flex-shrink-0"
            style={{
              width: `${spineW}px`,
              backgroundColor: coverColor.dark,
            }}
          >
            {/* Fabric grain texture on spine */}
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255,255,255,0.3) 1px, rgba(255,255,255,0.3) 2px)`,
              }}
            />
            {/* Horizontal ridge lines — like a real book spine */}
            {isLarge && (
              <>
                <div className="absolute left-[3px] right-[3px] top-[15%] h-px bg-white/15" />
                <div className="absolute left-[3px] right-[3px] top-[17%] h-px bg-black/10" />
                <div className="absolute left-[3px] right-[3px] top-[83%] h-px bg-white/15" />
                <div className="absolute left-[3px] right-[3px] top-[85%] h-px bg-black/10" />
              </>
            )}
            {/* Inner edge shadow */}
            <div className="absolute inset-y-0 right-0 w-px bg-black/20" />
          </div>

          {/* Cover — textured material surface */}
          <div
            className="relative flex-1 overflow-hidden"
            style={{
              backgroundColor: coverColor.bg,
              aspectRatio: '3 / 4',
            }}
          >
            {/* Fabric/cloth grain texture */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: [
                  // Horizontal weave
                  `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 3px)`,
                  // Vertical weave
                  `repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 3px)`,
                ].join(', '),
              }}
            />

            {/* Subtle noise texture overlay */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'repeat',
                backgroundSize: '128px 128px',
              }}
            />

            {/* Gutter shadow — left edge where cover meets spine */}
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-black/[0.12] to-transparent pointer-events-none z-10" />

            {/* === Cover content layout === */}
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-3">
              {/* Embossed title — above the photo window */}
              <p
                className={cn(
                  'font-hand font-bold text-center truncate w-full mb-2',
                  isLarge ? 'text-[19px]' : 'text-[13px]',
                )}
                style={{
                  color: coverColor.text,
                  textShadow: `0 1px 0 ${coverColor.dark}, 0 -1px 0 rgba(255,255,255,0.1)`,
                }}
              >
                {album.title}
              </p>

              {/* Photo window — inset rectangle with corner mounts */}
              <div
                className="relative w-full flex-1 min-h-0"
                style={{
                  maxWidth: `calc(100% - ${windowInset}px)`,
                  maxHeight: isLarge ? '55%' : '50%',
                }}
              >
                {/* Window border — slightly recessed */}
                <div
                  className="absolute inset-0"
                  style={{
                    boxShadow: `inset 0 1px 3px rgba(0,0,0,0.3), inset 0 -1px 0 rgba(255,255,255,0.08)`,
                    backgroundColor: coverColor.dark,
                  }}
                />

                {/* Photo inside the window */}
                {album.image ? (
                  <img
                    src={album.image}
                    alt={album.title}
                    className="relative w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="relative w-full h-full flex items-center justify-center" style={{ backgroundColor: coverColor.dark }}>
                    <svg width={isLarge ? 32 : 20} height={isLarge ? 32 : 20} viewBox="0 0 32 32" fill="none" className="opacity-30">
                      <rect x="4" y="3" width="14" height="17" fill="white" stroke="white" strokeWidth="1" />
                      <rect x="14" y="8" width="14" height="17" fill="white" stroke="white" strokeWidth="1" opacity="0.6" />
                    </svg>
                  </div>
                )}

                {/* L-shaped photo corner mounts */}
                <PhotoCorner position="top-left" isLarge={isLarge} color={coverColor.text} />
                <PhotoCorner position="top-right" isLarge={isLarge} color={coverColor.text} />
                <PhotoCorner position="bottom-left" isLarge={isLarge} color={coverColor.text} />
                <PhotoCorner position="bottom-right" isLarge={isLarge} color={coverColor.text} />
              </div>

              {/* Photo count — below the window */}
              <p
                className={cn(
                  'font-hand text-center mt-2',
                  isLarge ? 'text-[13px]' : 'text-[10px]',
                )}
                style={{
                  color: coverColor.text,
                  opacity: 0.7,
                }}
              >
                {photoCount} {photoCount === 1 ? 'photo' : 'photos'}
              </p>
            </div>

            {/* Bottom edge highlight — simulates light catching the cover edge */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10 pointer-events-none z-10" />
          </div>
        </div>

        {/* Page edges peeking from the right — cream-colored lines */}
        <div
          className="absolute top-[3px] bottom-[3px] -right-[3px] w-[4px] z-[-1]"
          style={{
            background: `repeating-linear-gradient(to bottom, 
              #FAF4E8 0px, #FAF4E8 1.5px, 
              #E8E0D0 1.5px, #E8E0D0 2px,
              #FAF4E8 2px, #FAF4E8 3.5px,
              #E8E0D0 3.5px, #E8E0D0 4px
            )`,
            boxShadow: '1px 0 2px rgba(0,0,0,0.08)',
          }}
        />
        {/* Second layer of pages — slightly offset for depth */}
        <div
          className="absolute top-[5px] bottom-[5px] -right-[5px] w-[2px] z-[-2]"
          style={{
            background: '#E8E0D0',
            boxShadow: '1px 0 1px rgba(0,0,0,0.05)',
          }}
        />
      </div>

      {/* Author row — below the album, outside the book */}
      {showAuthor && (
        <button
          onClick={handleAuthorClick}
          className="flex items-center gap-1.5 mt-2 group"
        >
          <div className="w-5 h-5 rounded-full overflow-hidden bg-cream-dark border border-pol-pink/30 flex-shrink-0">
            {avatar ? (
              <img src={avatar} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pol-pink/40 to-pol-lilac/40" />
            )}
          </div>
          <span className="text-[12px] text-pol-text-secondary group-hover:text-pol-text transition-colors truncate">
            {displayName}
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * Decorative L-shaped photo corner mount — like the brass/gold corners
 * used to hold photos in old-school albums.
 */
function PhotoCorner({ position, isLarge, color }: {
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  isLarge: boolean;
  color: string;
}) {
  const size = isLarge ? 12 : 8;
  const thickness = isLarge ? 3 : 2;
  const offset = isLarge ? -2 : -1;

  const positionStyles: React.CSSProperties = {
    position: 'absolute',
    width: size,
    height: size,
    zIndex: 20,
  };

  // Position at corners
  if (position.includes('top')) positionStyles.top = offset;
  if (position.includes('bottom')) positionStyles.bottom = offset;
  if (position.includes('left')) positionStyles.left = offset;
  if (position.includes('right')) positionStyles.right = offset;

  // Determine which borders to show for the L-shape
  const borderStyle = {
    borderColor: color,
    borderWidth: 0,
    borderStyle: 'solid' as const,
    opacity: 0.6,
  };

  if (position === 'top-left') {
    borderStyle.borderWidth = thickness;
    Object.assign(borderStyle, { borderRightWidth: 0, borderBottomWidth: 0 });
  } else if (position === 'top-right') {
    borderStyle.borderWidth = thickness;
    Object.assign(borderStyle, { borderLeftWidth: 0, borderBottomWidth: 0 });
  } else if (position === 'bottom-left') {
    borderStyle.borderWidth = thickness;
    Object.assign(borderStyle, { borderRightWidth: 0, borderTopWidth: 0 });
  } else {
    borderStyle.borderWidth = thickness;
    Object.assign(borderStyle, { borderLeftWidth: 0, borderTopWidth: 0 });
  }

  return <div style={{ ...positionStyles, ...borderStyle }} />;
}
